import React from "react";
import { useState } from "react";
import { Form, Drawer, DrawerProps } from "antd";
import { validateFields } from "../../../../../config/Global";
import { assignErrorToInput } from "../../../../../store/api";
import SVGIcon from "../../../../../utils/SVGIcon";
import Config from "../../../../../config/Config";
import { createRecord } from "../../utils/slice";
import FormComponent from "./FormComponent";
import { useAppDispatch } from "../../../../../utils/hooks/hooks";
import CancelButton from "../../../../../components/Buttons/CancelButton";
import SaveButton from "../../../../../components/Buttons/SaveButton";

interface AddDrawerProps extends DrawerProps {
  titleName: string;
  close: () => void;
}

/**
 * AddComponent component for adding a new record to career category.
 *
 * @param {AddDrawerProps} props - The props for the component.
 * @param {string} props.titleName - The name of the title.
 * @param {() => void} props.close - The function to close the drawer.
 * @param {boolean} props.open - The state of the drawer.
 * @param {DrawerProps} props.rest - The rest of the drawer props.
 * @returns {JSX.Element} The rendered component.
 */
const AddComponent: React.FC<AddDrawerProps> = ({
  titleName,
  close,
  open,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const drawerClose = () => {
    close();
    form.resetFields();
    setDisabled(true);
  };

  /**
   * Function to validate the form fields.
   */
  const validateForm = () => {
    validateFields(form, setDisabled);
  };

  /**
   * Function to handle the form submission.
   *
   * @param {any} data - The form data.
   */
  const handleFormSubmit = (data: any) => {
    setSaving(true);

    dispatch(createRecord(data))
      .then(() => {
        drawerClose();
      })
      .catch((error: any) => {
        assignErrorToInput(form, error?.STATUS);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Drawer
      title={`Add ${titleName}`}
      open={open}
      width={"70%"}
      placement="right"
      onClose={drawerClose}
      destroyOnClose
      closeIcon={<SVGIcon icon="close" color={Config.themePrimaryBtn} />}
      footer={[
        <CancelButton key={2} onClick={drawerClose} />,
        <SaveButton
          key={1}
          form="addForm"
          disabled={disabled}
          loading={saving}
        />,
      ]}
      {...rest}
    >
      <FormComponent
        form={form}
        id="addForm"
        handleSubmit={handleFormSubmit}
        onValuesChange={validateForm}
      />
    </Drawer>
  );
};

export default AddComponent;
